import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { Copy, AlertTriangle } from "lucide-react";
import { Room, getTaskText } from "@/types/checklist";

interface ApplyToRoomsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskText: string;
  currentRoomName: string;
  rooms: Room[];
  onApply: (selectedRoomNames: string[], skippedRoomNames: string[]) => void;
}

const ApplyToRoomsModal = ({
  open,
  onOpenChange,
  taskText,
  currentRoomName,
  rooms,
  onApply,
}: ApplyToRoomsModalProps) => {
  const isMobile = useIsMobile();
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [skippedDuplicates, setSkippedDuplicates] = useState<string[]>([]);

  // Filter out current room and rooms without names
  const availableRooms = rooms.filter(
    (room) => room.name.trim() && room.name !== currentRoomName
  );

  // Check which rooms already have a task with the same name (case-insensitive)
  const roomsWithDuplicate = useMemo(() => {
    const normalizedTaskText = taskText.trim().toLowerCase();
    return availableRooms
      .filter((room) =>
        room.tasks.some(
          (task) => getTaskText(task).trim().toLowerCase() === normalizedTaskText
        )
      )
      .map((room) => room.name);
  }, [availableRooms, taskText]);

  const handleToggleRoom = (roomName: string) => {
    const hasDuplicate = roomsWithDuplicate.includes(roomName);
    
    if (selectedRooms.includes(roomName)) {
      // Deselecting - also remove from skipped if it was there
      setSelectedRooms((prev) => prev.filter((r) => r !== roomName));
      setSkippedDuplicates((prev) => prev.filter((r) => r !== roomName));
    } else {
      // Selecting
      setSelectedRooms((prev) => [...prev, roomName]);
      // If it has a duplicate, default to skipped (user can toggle to add anyway)
      if (hasDuplicate && !skippedDuplicates.includes(roomName)) {
        setSkippedDuplicates((prev) => [...prev, roomName]);
      }
    }
  };

  const handleToggleSkip = (roomName: string) => {
    setSkippedDuplicates((prev) =>
      prev.includes(roomName)
        ? prev.filter((r) => r !== roomName)
        : [...prev, roomName]
    );
  };

  const handleSelectAll = () => {
    if (selectedRooms.length === availableRooms.length) {
      setSelectedRooms([]);
      setSkippedDuplicates([]);
    } else {
      const allRoomNames = availableRooms.map((r) => r.name);
      setSelectedRooms(allRoomNames);
      // Default: skip all rooms that have duplicates
      setSkippedDuplicates(roomsWithDuplicate);
    }
  };

  const handleApply = () => {
    // Rooms to actually apply = selected minus skipped
    const roomsToApply = selectedRooms.filter((r) => !skippedDuplicates.includes(r));
    const roomsSkipped = selectedRooms.filter((r) => skippedDuplicates.includes(r));
    onApply(roomsToApply, roomsSkipped);
    setSelectedRooms([]);
    setSkippedDuplicates([]);
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedRooms([]);
    setSkippedDuplicates([]);
    onOpenChange(false);
  };

  const roomsToApplyCount = selectedRooms.filter((r) => !skippedDuplicates.includes(r)).length;
  const roomsToSkipCount = selectedRooms.filter((r) => skippedDuplicates.includes(r)).length;

  const content = (
    <div className="flex flex-col h-full">
      {/* Task Preview */}
      <div className="px-4 py-3 bg-muted/50 rounded-lg mb-4">
        <p className="text-xs text-muted-foreground mb-1">Task to apply:</p>
        <p className="text-sm font-medium line-clamp-2">{taskText}</p>
      </div>

      {/* Room List */}
      <div className="flex-1 min-h-0">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium">Select rooms:</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            className="h-8 text-xs"
          >
            {selectedRooms.length === availableRooms.length
              ? "Deselect All"
              : "Select All"}
          </Button>
        </div>

        <ScrollArea className={isMobile ? "h-[calc(100vh-420px)]" : "h-[300px]"}>
          <div className="space-y-2 pr-4">
            {availableRooms.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No other rooms available.
              </p>
            ) : (
              availableRooms.map((room) => {
                const hasDuplicate = roomsWithDuplicate.includes(room.name);
                const isSelected = selectedRooms.includes(room.name);
                const isSkipped = skippedDuplicates.includes(room.name);

                return (
                  <div key={room.name} className="space-y-1">
                    <label
                      className={`flex items-center gap-3 p-3 rounded-lg border bg-background hover:bg-accent/50 cursor-pointer transition-colors min-h-[48px] ${
                        isSelected && !isSkipped ? "border-primary/50 bg-primary/5" : ""
                      } ${isSelected && isSkipped ? "border-amber-500/50 bg-amber-50/50" : ""}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleRoom(room.name)}
                        className="h-5 w-5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{room.name}</span>
                          {hasDuplicate && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 shrink-0 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Duplicate
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {room.tasks.length} task{room.tasks.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </label>

                    {/* Duplicate warning with skip/add option */}
                    {hasDuplicate && isSelected && (
                      <div className="ml-8 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                        <p className="text-amber-800 mb-2">
                          This room already has a task named "{taskText.length > 30 ? taskText.substring(0, 30) + "..." : taskText}"
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={isSkipped ? "outline" : "default"}
                            className={`h-9 flex-1 ${!isSkipped ? "bg-primary" : ""}`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (isSkipped) handleToggleSkip(room.name);
                            }}
                          >
                            Add anyway
                          </Button>
                          <Button
                            size="sm"
                            variant={isSkipped ? "default" : "outline"}
                            className={`h-9 flex-1 ${isSkipped ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isSkipped) handleToggleSkip(room.name);
                            }}
                          >
                            Skip
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Selection Summary */}
      {selectedRooms.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs text-muted-foreground">
            {roomsToApplyCount} room{roomsToApplyCount !== 1 ? "s" : ""} will receive this task
            {roomsToSkipCount > 0 && (
              <span className="text-amber-600">
                {" "}• {roomsToSkipCount} skipped (duplicate)
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex gap-2 w-full">
      <Button
        variant="outline"
        onClick={handleClose}
        className="flex-1 h-12 sm:h-10"
      >
        Cancel
      </Button>
      <Button
        onClick={handleApply}
        disabled={roomsToApplyCount === 0}
        className="flex-1 h-12 sm:h-10"
      >
        <Copy className="w-4 h-4 mr-2" />
        Apply to {roomsToApplyCount || 0} Room{roomsToApplyCount !== 1 ? "s" : ""}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[95vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>Apply this task to other rooms</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 flex-1 overflow-hidden">{content}</div>
          <DrawerFooter className="pt-4">{footer}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Apply this task to other rooms</DialogTitle>
        </DialogHeader>
        {content}
        <DialogFooter className="sm:justify-stretch">{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyToRoomsModal;
